import { ArrowRightOutlined } from '@ant-design/icons';
import { Card, Col, Divider, Flex, Row, Tabs } from 'antd';
import CustomLoader from 'components/CustomLoader';
import DrawerComponent from 'components/Drawer';
import TagComponent from 'components/Tags';
import TaskInfo from 'components/TaskInfo';
import { useRoleBasedCountryFilter } from 'components/UniversalFilters/RBCountryFilter';
import { TDashboard } from 'hooks/useDashboardQuery';
import useSeasonIndex from 'hooks/useSeasonIndex';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSeasonByIdQuery from 'screens/awd-dashboard/hooks/queries/useSeasonByIdQuery';
import { getStatusTagColor } from 'screens/farmer/helper';
import { Season } from 'types/common';
import { getStakeholderTypeForCountry, renderDate } from 'utils/helpers';
import { getGroupType } from './DashboardTable';

type DetailDrawerProps = {
  open: boolean;
  data?: TDashboard;
  onClose: () => void;
  isSalesGroup?: boolean;
};

const DetailDrawer = ({
  open,
  data,
  onClose,
  isSalesGroup = false,
}: DetailDrawerProps) => {
  const { data: seasonData, isLoading } = useSeasonByIdQuery(data?.season?.id);
  const [activeTab, setActiveTab] = useState('1');

  const { t } = useTranslation();

  const tabItems = [
    {
      key: '1',
      label: t('groupDetailPage.header'),
      children: (
        <GroupDetailsContent
          data={data}
          seasonData={seasonData}
          isSalesGroup={isSalesGroup}
        />
      ),
    },
    {
      key: '2',
      label: t('seasonProgressBar.participatingFarmers'),
      children: <ParticipatingFarmersContent data={seasonData} />,
    },
  ];

  const handleClose = () => {
    onClose?.();
    setActiveTab('1');
  };

  return (
    <DrawerComponent
      open={open}
      title={
        isSalesGroup
          ? t('salesGroups.salesGroupDetails')
          : t('dashboard.plantingGroupDetails')
      }
      content={
        <>
          {isLoading ? (
            <div className="loader-container">
              <CustomLoader size="extra-large" />
            </div>
          ) : (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={tabItems}
              defaultActiveKey="1"
            />
          )}
        </>
      }
      onClose={handleClose}
    />
  );
};

const GroupDetailsContent = ({
  data,
  seasonData,
  isSalesGroup,
}: {
  data?: TDashboard;
  seasonData?: Season;
  isSalesGroup: boolean;
}) => {
  const [country] = useRoleBasedCountryFilter();
  const { t } = useTranslation();
  const { data: seasonIndexData } = useSeasonIndex(country);

  const seasonIndex = seasonIndexData?.find(
    (season) => season.id === data?.season?.seasonTypeId,
  )?.seasonCode;

  return (
    <Card>
      <Row gutter={[16, 24]}>
        <Col span={12}>
          <TaskInfo
            header={
              isSalesGroup
                ? t('salesGroups.salesGroupName')
                : t('dashboard.plantingGroupName')
            }
          >
            {seasonData?.name}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('common.filters.agronomist')}>
            {data?.agronomistName}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('farmerGroupList.seasonStatus')}>
            <TagComponent
              label={data?.season?.status}
              color={getStatusTagColor(data?.season?.status)}
            />
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('salesGroups.dap')}>{data?.dap}</TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('seasonProgressBar.estimatedPlantingDate')}>
            {renderDate(data?.season?.estimatedStartDate)}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('seasonProgressBar.estimatedHarvestDate')}>
            {renderDate(data?.season?.estimatedEndDate)}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('dashboard.creationDate')}>
            {renderDate(data?.season?.createdDate)}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('addFgForm.createdBy')}>
            {data?.season?.createdBy}
          </TaskInfo>
        </Col>
        {isSalesGroup ? null : (
          <Col span={12}>
            <TaskInfo header={t('groupsPage.groupType')}>
              {getGroupType(data)}
            </TaskInfo>
          </Col>
        )}
        <Col span={12}>
          <TaskInfo header={t('dashboard.seasonIndex')}>{seasonIndex}</TaskInfo>
        </Col>

        <Divider style={{ margin: 0 }} />

        <Col span={12}>
          <TaskInfo header={t('farmerDetailsPage.province')}>
            {data?.address?.stateOrProvince}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('farmerDetailsPage.district')}>
            {data?.address?.district}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('farmerDetailsPage.subDistrict')}>
            {data?.address?.subDistrict}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('farmerDetailsPage.village')}>
            {data?.address?.cityOrVillage}
          </TaskInfo>
        </Col>

        <Divider style={{ margin: 0 }} />

        <Col span={12}>
          <TaskInfo header={t('farmerDetailsPage.farmerGroupLeader')}>
            <a
              className="clickable link-text"
              href={
                '/stakeholders/farmer-group-leader?leaderId=' + data?.leaderId
              }
              target="__blank"
            >
              {data?.leaderName} <ArrowRightOutlined />
            </a>
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={getStakeholderTypeForCountry(country, 'PB')}>
            {data?.paddyBuyerName}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('dashboard.participatingHectares')}>
            {data?.participatedArea}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('dashboard.participatingFarmers')}>
            {data?.participatedFarmers}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('dashboard.seedingAge')}>
            {data?.season?.seedlingAge}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('seasonProgressBar.variety')}>
            {data?.season?.variety}
          </TaskInfo>
        </Col>
        <Col span={12}>
          <TaskInfo header={t('seasonProgressBar.avgMaturityAge')}>
            {data?.season?.varietyMatureDay || 'N/A'}
          </TaskInfo>
        </Col>
      </Row>
    </Card>
  );
};

const ParticipatingFarmersContent = ({ data }: { data?: Season }) => {
  const { t } = useTranslation();
  return (
    <Flex gap={12} vertical>
      {data?.seasonParticipantses?.map((participant, index) => (
        <Card key={participant?.id + index}>
          <Flex gap={8} vertical>
            <Flex justify="space-between">
              <span>{participant?.farmer?.name}</span>
              <span>
                <TagComponent
                  label={participant?.farmer?.status}
                  color={getStatusTagColor(participant?.farmer?.status)}
                />
              </span>
            </Flex>
            <TaskInfo header={t('dashboard.participatingHectares')}>
              {Number(participant?.area)?.toFixed(0)}/
              {Number(participant?.farmer?.area).toFixed(0)}
            </TaskInfo>
          </Flex>
        </Card>
      ))}
    </Flex>
  );
};

export default DetailDrawer;
